import { BASE } from '../basedata/constants'
import { fetchWithApiKey } from './apiCall'
import { getCurrentTenantId } from './session'

export const SELECTED_GATE_STORAGE_KEY = 'selected_gate'
export const SELECTED_GATE_CODE_STORAGE_KEY = 'gate'

export type GateOption = {
  id: string
  integration_code: string | null
  alias: string | null
  label: string
}

export type GateSelectOption = {
  label: string
  value: string | null
  gateId: string
  alias: string | null
  gate: GateOption
}

export type TicketGateSource = {
  gateId?: string | number | null
  gateCode?: string | number | null
  integration_code?: string | number | null
  integrationCode?: string | number | null
  ingPortao?: string | number | null
  gate?: string | number | null
}

const toText = (value?: string | number | null): string =>
  value === undefined || value === null ? '' : `${value}`.trim()

export const normalizeGateCode = (code?: string | number | null): string =>
  toText(code).replace(/\s+/g, ' ').toLowerCase()

export const splitGateCodes = (value?: string | number | null): string[] => {
  const normalizedValue = toText(value)

  if (!normalizedValue) {
    return []
  }

  return normalizedValue
    .split(/[,\n;]+/)
    .flatMap((chunk) => chunk.split(/\s+(?:ou|e)\s+/i))
    .map((code) => normalizeGateCode(code))
    .filter(Boolean)
}

export const getGateCode = (gate?: GateOption | null): string =>
  toText(gate?.integration_code)

export const getGateLabel = (gate?: GateOption | null): string =>
  toText(gate?.label) || 'Portão sem código'

export const buildGateSelectOptions = (
  gates: GateOption[]
): GateSelectOption[] =>
  gates
    .map((gate) => ({
      label: getGateLabel(gate),
      value: gate.integration_code,
      gateId: gate.id,
      alias: gate.alias,
      gate
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }))

export const findGateByCode = (
  gates: GateOption[],
  code?: string | number | null
): GateOption | null => {
  const normalizedCode = normalizeGateCode(code)

  if (!normalizedCode) {
    return null
  }

  return (
    gates.find((gate) => normalizeGateCode(gate.integration_code) === normalizedCode) ||
    null
  )
}

export const getTicketGateCode = (ticket?: TicketGateSource | null): string => {
  if (!ticket) {
    return ''
  }

  return (
    toText(ticket.gateId) ||
    toText(ticket.gateCode) ||
    toText(ticket.integration_code) ||
    toText(ticket.integrationCode) ||
    toText(ticket.ingPortao) ||
    toText(ticket.gate)
  )
}

export const isCorrectGate = (
  ticketGateCode?: string | number | null,
  selectedGateCode?: string | number | null
): boolean => {
  const selectedCodes = splitGateCodes(selectedGateCode)
  const ticketCodes = splitGateCodes(ticketGateCode)

  if (!selectedCodes.length || !ticketCodes.length) {
    return false
  }

  return ticketCodes.some((code) => selectedCodes.includes(code))
}

export const fetchDefaultGateOptions = async (): Promise<GateOption[]> => {
  const response = await fetchWithApiKey(
    `${BASE.ticket_api.base_url}${BASE.ticket_api.gate_template_default}`,
    {
      method: 'GET'
    }
  )

  if (!response.ok) {
    throw new Error('Falha ao carregar portões')
  }

  const payload = await response.json()
  const gates = Array.isArray(payload) ? payload : []
  const tenantId = getCurrentTenantId()

  return gates
    .map((gate) => {
      let label: string

      if (tenantId === 'saopaulo-oj8c8') {
        label = toText(gate?.alias)
      } else if (tenantId === 'santos-opsh9') {
        label = toText(gate?.integration_code)
      } else {
        label = toText(gate?.label)
      }

      return {
        id: toText(gate?.id),
        integration_code:
          gate?.integration_code === undefined || gate?.integration_code === null
            ? null
            : toText(gate.integration_code),
        alias:
          gate?.alias === undefined || gate?.alias === null
            ? null
            : toText(gate.alias),
        label
      }
    })
}
